import { useMemo, useState } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { EstateItem, EstateStageJson } from '@types';
import { assignLotsToStage, compareLotCodes, validateStageConfiguration } from '@utils/stages';
import { Button } from '@components/ui/Button';

interface StageManagerProps {
  lots: EstateItem[];
  stages: EstateStageJson[];
  onChange: (stages: EstateStageJson[]) => void;
}

const UNASSIGNED = '__unassigned__';

export const StageManager = ({ lots, stages, onChange }: StageManagerProps) => {
  const [search, setSearch] = useState('');
  const [selectedLotCodes, setSelectedLotCodes] = useState<string[]>([]);
  const [bulkTarget, setBulkTarget] = useState(UNASSIGNED);

  const sortedLots = useMemo(
    () => [...lots].sort((left, right) => compareLotCodes(left.code, right.code)),
    [lots]
  );
  const filteredLots = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return query
      ? sortedLots.filter((lot) => lot.code.toLocaleLowerCase().includes(query))
      : sortedLots;
  }, [search, sortedLots]);
  const validation = useMemo(() => validateStageConfiguration(stages, lots), [lots, stages]);
  const assignmentByCode = useMemo(() => {
    const result = new Map<string, number>();
    stages.forEach((stage, stageIndex) => {
      (stage.lots ?? []).forEach((rawCode) => {
        const code = String(rawCode);
        if (!result.has(code)) result.set(code, stageIndex);
      });
    });
    return result;
  }, [stages]);

  const updateStage = (stageIndex: number, patch: Partial<EstateStageJson>) => {
    onChange(stages.map((stage, index) => (index === stageIndex ? { ...stage, ...patch } : stage)));
  };

  const addStage = () => {
    let sequence = stages.length + 1;
    const names = new Set(stages.map((stage) => stage.name.trim().toLocaleLowerCase()));
    while (names.has(`stage ${sequence}`)) sequence += 1;
    onChange([
      ...stages,
      {
        name: `Stage ${sequence}`,
        release_status: 'available',
        title_status: 'scheduled',
        show_price_publicly: true,
        lots: [],
      },
    ]);
  };

  const removeStage = (stageIndex: number) => {
    onChange(stages.filter((_, index) => index !== stageIndex));
    setBulkTarget(UNASSIGNED);
  };

  const assignCodes = (codes: string[], targetStageIndex: number | null) => {
    onChange(assignLotsToStage(stages, codes, targetStageIndex));
  };

  const applyBulkAssignment = () => {
    if (selectedLotCodes.length === 0) return;
    assignCodes(
      selectedLotCodes,
      bulkTarget === UNASSIGNED ? null : Number.parseInt(bulkTarget, 10)
    );
    setSelectedLotCodes([]);
  };

  const filteredCodes = filteredLots.map((lot) => lot.code);
  const allFilteredSelected =
    filteredCodes.length > 0 && filteredCodes.every((code) => selectedLotCodes.includes(code));

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-gray-800">
            {validation.assignedCount} of {lots.length} lots assigned
          </p>
          <p className="text-xs text-gray-500">
            A lot can belong to one stage. Unassigned lots remain in the estate without a stage.
          </p>
        </div>
        <Button type="button" size="sm" variant="outlined" onClick={addStage}>
          <PlusIcon className="h-4 w-4" />
          Add stage
        </Button>
      </div>

      {validation.errors.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800">
          <p className="mb-1 font-bold">Fix before continuing</p>
          <ul className="list-disc space-y-1 pl-5">
            {validation.errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {validation.warnings.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          <ul className="list-disc space-y-1 pl-5">
            {validation.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      )}

      {stages.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-200 px-4 py-8 text-center">
          <p className="text-sm font-medium text-gray-700">No stages configured</p>
          <p className="mt-1 text-xs text-gray-500">
            Add a stage, set its release details, then assign lots below.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {stages.map((stage, stageIndex) => {
            const assignedCount = (stage.lots ?? []).length;
            return (
              <div key={stageIndex} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-bold uppercase text-gray-500">
                      Stage {stageIndex + 1}
                    </p>
                    <p className="text-xs text-gray-500">
                      {assignedCount} lot{assignedCount === 1 ? '' : 's'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeStage(stageIndex)}
                    className="rounded p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                    aria-label={`Remove ${stage.name || `stage ${stageIndex + 1}`}`}
                    title="Remove stage and leave its lots unassigned"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <label className="text-xs font-bold uppercase text-gray-700">
                    Name
                    <input
                      type="text"
                      value={stage.name}
                      onChange={(event) => updateStage(stageIndex, { name: event.target.value })}
                      className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-normal normal-case focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </label>

                  <label className="text-xs font-bold uppercase text-gray-700">
                    Release status
                    <select
                      value={stage.release_status ?? 'available'}
                      onChange={(event) => {
                        const releaseStatus = event.target.value as 'available' | 'future';
                        updateStage(stageIndex, {
                          release_status: releaseStatus,
                          show_price_publicly:
                            releaseStatus === 'future'
                              ? false
                              : (stage.show_price_publicly ?? true),
                        });
                      }}
                      className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-normal normal-case focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="available">Available</option>
                      <option value="future">Future</option>
                    </select>
                  </label>

                  <label className="text-xs font-bold uppercase text-gray-700">
                    Title status
                    <select
                      value={stage.title_status ?? ''}
                      onChange={(event) =>
                        updateStage(stageIndex, {
                          title_status: event.target.value
                            ? (event.target.value as 'scheduled' | 'titled')
                            : undefined,
                        })
                      }
                      className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-normal normal-case focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Not set</option>
                      <option value="scheduled">Scheduled</option>
                      <option value="titled">Titled</option>
                    </select>
                  </label>

                  <label className="text-xs font-bold uppercase text-gray-700">
                    Release date
                    <input
                      type="date"
                      value={stage.release_date?.slice(0, 10) ?? ''}
                      onChange={(event) =>
                        updateStage(stageIndex, { release_date: event.target.value || null })
                      }
                      className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-normal normal-case focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </label>
                </div>

                <label className="mt-3 flex cursor-pointer items-center gap-2 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={stage.show_price_publicly ?? true}
                    onChange={(event) =>
                      updateStage(stageIndex, { show_price_publicly: event.target.checked })
                    }
                    className="h-4 w-4 rounded border-gray-300 text-gold focus:ring-gold"
                  />
                  Show prices publicly for this stage
                </label>
              </div>
            );
          })}
        </div>
      )}

      <div className="border-t border-gray-200 pt-4">
        <div className="mb-3 flex flex-wrap items-end gap-2">
          <label className="min-w-40 flex-1 text-xs font-bold uppercase text-gray-700">
            Find lots
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search lot code"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-normal normal-case focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </label>
          <button
            type="button"
            onClick={() =>
              setSelectedLotCodes((current) =>
                allFilteredSelected
                  ? current.filter((code) => !filteredCodes.includes(code))
                  : Array.from(new Set([...current, ...filteredCodes]))
              )
            }
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-bold text-gray-700 hover:bg-gray-50"
          >
            {allFilteredSelected ? 'Clear shown' : 'Select shown'}
          </button>
        </div>

        <div className="mb-3 flex flex-wrap items-end gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <label className="min-w-44 flex-1 text-xs font-bold uppercase text-gray-700">
            Assign selected ({selectedLotCodes.length})
            <select
              value={bulkTarget}
              onChange={(event) => setBulkTarget(event.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-normal normal-case focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value={UNASSIGNED}>Leave unassigned</option>
              {stages.map((stage, index) => (
                <option key={index} value={index}>
                  {stage.name || `Stage ${index + 1}`}
                </option>
              ))}
            </select>
          </label>
          <Button
            type="button"
            size="sm"
            onClick={applyBulkAssignment}
            disabled={selectedLotCodes.length === 0}
          >
            Apply
          </Button>
        </div>

        <div className="max-h-80 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200">
          {filteredLots.length === 0 ? (
            <p className="p-4 text-center text-sm text-gray-500">No lots match this search.</p>
          ) : (
            filteredLots.map((lot) => {
              const assignedStageIndex = assignmentByCode.get(lot.code);
              return (
                <div key={lot.handleid} className="flex items-center gap-3 bg-white px-3 py-2">
                  <input
                    type="checkbox"
                    checked={selectedLotCodes.includes(lot.code)}
                    onChange={(event) =>
                      setSelectedLotCodes((current) =>
                        event.target.checked
                          ? [...current, lot.code]
                          : current.filter((code) => code !== lot.code)
                      )
                    }
                    aria-label={`Select lot ${lot.code}`}
                    className="h-4 w-4 rounded border-gray-300 text-gold focus:ring-gold"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-800">Lot {lot.code}</p>
                    <p className="text-xs capitalize text-gray-500">{lot.status}</p>
                  </div>
                  <select
                    value={
                      assignedStageIndex === undefined ? UNASSIGNED : String(assignedStageIndex)
                    }
                    onChange={(event) =>
                      assignCodes(
                        [lot.code],
                        event.target.value === UNASSIGNED
                          ? null
                          : Number.parseInt(event.target.value, 10)
                      )
                    }
                    aria-label={`Stage for lot ${lot.code}`}
                    className="w-44 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value={UNASSIGNED}>Unassigned</option>
                    {stages.map((stage, index) => (
                      <option key={index} value={index}>
                        {stage.name || `Stage ${index + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
